import {
  AdminGroups, AdminUsers, Campaigns, GridShow,
} from 'Interfaces/Admin';
import { collectDescendants, collectGroupUsers } from 'reducers/admin/selectors';

/**
 *  Flattens the collections into the rows the grid displays. Ported from
 *  DetailsView's getGroupColumnDefinition / getUserColumnDefinition /
 *  getCampaignColumnDefinition, which built both the columns and the data.
 */

export interface UserRow {
  id: number;
  userName: string;
  active: boolean;
  fullName: string;
  email: string;
  roles: string;
  groupName: string;
  assignedLocs: string;
  lastLogin: string | null;
}

export interface GroupRow {
  id: number;
  name: string;
  active: boolean;
  formlist: string;
  hierarchy: string;
  subgroups: string;
  users: number;
}

export interface CampaignRow {
  id: number;
  name: string;
  campaignTarget: number;
  campaignResponses: number;
  campaignStatus: string;
  campaignStartDate: string | null;
  campaignEndDate: string | null;
  formlist: string;
  hierarchy: string;
}

interface Context {
  groups: AdminGroups;
  users: AdminUsers;
  hierarchies: { id: number; name: string }[];
}

/**
 *  Which group the scoped views ("my users", "my groups") are relative to:
 *  the selected group, falling back to the signed-in admin's own group.
 */
export const scopeGroupId = (
  selectedGroupId: number | null, accountGroupId: number | null,
): number | null => selectedGroupId ?? accountGroupId;

export const buildUserRows = (
  ctx: Context, show: GridShow, groupId: number | null,
): UserRow[] => {
  const { users, groups } = ctx;
  let visible = users;

  if (show !== 'all' && groupId !== null) {
    visible = collectGroupUsers(users, groups, groupId, show === 'groupSubgroupUsers', true);
  } else if (show !== 'all') {
    visible = [];
  }

  return visible.map((user) => ({
    id: user.id,
    userName: user.userName,
    active: user.active,
    fullName: user.fullName || '',
    email: user.email || '',
    roles: user.roles,
    groupName: user.groupId
      ? groups.find((g) => g.id === user.groupId)?.name || ''
      : '',
    assignedLocs: (user.locationNames || []).join(', '),
    lastLogin: user.lastLogin,
  }));
};

export const buildGroupRows = (
  ctx: Context, show: GridShow, groupId: number | null,
): GroupRow[] => {
  const { groups, users, hierarchies } = ctx;
  const visible = show === 'all'
    ? groups
    : (groupId !== null ? collectDescendants(groups, groupId, true) : []);

  return visible.map((group) => ({
    id: group.id,
    name: group.name,
    active: group.active,
    formlist: group.formListName || '',
    hierarchy: hierarchies.find((h) => h.id === group.locationHierarchyId)?.name || '',
    subgroups: groups
      .filter((g) => g.parentId === group.id && g.active)
      .map((g) => g.name)
      .join(','),
    users: users.filter((u) => u.groupId === group.id).length,
  }));
};

export const buildCampaignRows = (
  campaigns: Campaigns, hierarchies: { id: number; name: string }[],
): CampaignRow[] => campaigns.map((campaign) => ({
  id: campaign.id,
  name: campaign.name,
  campaignTarget: campaign.invitedResponses || 0,
  campaignResponses: campaign.responses || 0,
  campaignStatus: campaign.campaign_status,
  campaignStartDate: campaign.campaign_start_date,
  campaignEndDate: campaign.campaign_end_date,
  formlist: campaign.campaignFormList || '',
  hierarchy: hierarchies.find((h) => h.id === campaign.locationHierarchyId)?.name || '',
}));
